// biome-ignore-all lint/style/useNamingConvention: allow camel case

import {createSlice, type PayloadAction} from '@reduxjs/toolkit';
import type {FilterGroupType} from '~/GalleryPB/types/filters';
import type {GalleryData, Relative} from '~/GalleryPB/types/gallery';
import type {Template} from '~/GalleryPB/types/template';

export interface GalleryDataState {
  filterSections: Record<string, FilterGroupType>;
  categoryId: string;
  categoryPath: string | null;
  breadcrumbs: {
    text: string;
    href: string;
  }[];
  hasChildren?: boolean;
  parentId?: string;
  templates: {
    free: Template[];
    premium: Template[];
  };
  currentPage: number;
  pageCount: number;
  numTemplates?: number;
  relatives: Relative[];
  replaced: boolean;
  tracking: string;
  pageDisplayName?: string;
  previousCurrentPage?: number;
}

const initialState: GalleryDataState = {
  filterSections: {},
  /**
   * filterSections format: { [title: string]: object }
   * Inner object format:
   * {
   *   id: string
   *   filters: { [filterName: string]: {
   *     on: boolean (filter on/off status)
   *     available: boolean (filter availability status)
   *   } }
   * }
   */
  categoryId: '',
  categoryPath: '',
  breadcrumbs: [] /** breadcrumbs format: { text: string, href: string }[] */,
  hasChildren: false,
  parentId: '',
  templates: {
    free: [],
    premium: [],
  } /** templates format: { free: object[], premium: object[] } */,
  currentPage: 1,
  pageCount: 1,
  numTemplates: undefined,
  relatives: [],
  replaced: false,
  tracking: '',
  pageDisplayName: '',
  previousCurrentPage: 1,
};

const getBreadCrumbs = (breadcrumbs: Relative[]) => {
  let currentCategorypath = '';
  return breadcrumbs.map((bd) => {
    currentCategorypath = `${currentCategorypath}/${bd.categoryPath}`;
    return {
      text: bd.displayName,
      href: currentCategorypath,
    };
  });
};

export const galleryDataSlice = createSlice({
  name: 'galleryData',
  initialState,
  reducers: {
    initGalleryData: (state, action: PayloadAction<GalleryData>) => {
      const {
        filterSections: sections,
        categoryId = '',
        categoryPath,
        hasChildren,
        parentId,
        breadcrumbs,
        freeTemplates,
        premiumTemplates,
        page: currentPage,
        pageCount,
        total: numTemplates,
        relatives,
        replaced,
        tracking,
        pageDisplayName,
        previousCurrentPage,
      } = action.payload;
      /**
       * filterSections would be a list of filter data objects
       * Format for each filter data object:
       * {
       *   name: string (sluggy name uniquely identifying this chunk of filter options)
       *   display_name: string (section title, e.g. "Show Only," "Themes," etc.)
       *   filters: {
       *     name: string (sluggy name for the filter)
       *     display_name: string
       *     enabled: boolean
       *   }
       * }
       */
      const filterSections: Record<string, FilterGroupType> = {};
      sections.forEach((section) => {
        const title = section.filterSectionId;
        // Create filter section dictionary
        filterSections[title] = {
          display_name: section.displayName || 'Filter by',
          filters: {},
        };

        Object.entries(section.filters).forEach((filter) => {
          filterSections[title].filters[filter[1].filterId] = {
            display_name: filter[1].displayName,
            on: false,
            enabled: filter[1].enabled,
          };
        });
      });
      const templates = {
        free: freeTemplates.map((t, i) => ({...t, pos: t.pos || i})),
        premium: premiumTemplates,
      };

      return {
        ...state,
        filterSections,
        categoryId,
        categoryPath,
        hasChildren,
        parentId,
        templates,
        breadcrumbs: breadcrumbs ? getBreadCrumbs(breadcrumbs) : [],
        currentPage,
        pageCount,
        numTemplates,
        replaced,
        relatives: relatives || [],
        tracking: tracking || '',
        pageDisplayName,
        previousCurrentPage,
      };
    },
    setPreviousCurrentPage: (state, action: PayloadAction<number>) => {
      state.previousCurrentPage = action.payload;
    },
    updateActiveFilters: (
      state,
      action: PayloadAction<{sectionName?: string; filterName?: string; apiResponse: GalleryData}>
    ) => {
      const {
        sectionName, // the filter section to update
        filterName, // the filter to toggle
        apiResponse: {
          filterSections: filterSectionsFromApi,
          categoryId,
          categoryPath,
          hasChildren,
          parentId,
          breadcrumbs,
          freeTemplates,
          premiumTemplates,
          page: currentPage,
          pageCount,
          total: numTemplates,
          relatives,
          replaced,
          tracking,
        }, // the updated filter data from the API
      } = action.payload;
      const filterSections = JSON.parse(JSON.stringify(state.filterSections));

      // Toggle the filter
      if (sectionName && filterName && filterSections[sectionName]?.filters?.[filterName]) {
        filterSections[sectionName].filters[filterName].on =
          !filterSections[sectionName].filters[filterName].on;
      }
      Object.keys(filterSections).forEach((name) => {
        // Find matching filter section
        const apiFilterSection = filterSectionsFromApi.find((afs) => afs.filterSectionId === name);

        if (!apiFilterSection) return;

        // Extract filter names
        const filterNames = Object.keys(filterSections[name].filters);

        Object.values(filterNames).forEach((filter) => {
          const apiFilter = apiFilterSection.filters.find((f) => f.filterId === filter);
          if (!apiFilter && !filterSections[name].filters[filter].on) {
            // If the filter is not in the API response,
            // AND if the user doesn't already have the filter turned on,
            // disable that filter option
            filterSections[name].filters[filter].enabled = false;
          } else if (apiFilter && !filterSections[name].filters[filter].on) {
            filterSections[name].filters[filter].enabled = apiFilter.enabled;
          } else {
            filterSections[name].filters[filter].enabled = true;
          }
        });
      });

      const templates = {
        free: freeTemplates.map((t, i) => ({...t, pos: t.pos || i})),
        premium: premiumTemplates,
      };

      return {
        ...state,
        filterSections,
        categoryId,
        categoryPath,
        hasChildren,
        parentId,
        templates,
        breadcrumbs: breadcrumbs ? getBreadCrumbs(breadcrumbs) : [],
        currentPage,
        pageCount,
        numTemplates,
        replaced,
        relatives: relatives || [],
        tracking: tracking || '',
      };
    },
    resetFilters: (state, action: PayloadAction<{apiResponse: GalleryData}>) => {
      const {
        apiResponse: {
          filterSections: filterSectionsFromApi,
          categoryId,
          categoryPath,
          hasChildren,
          parentId,
          breadcrumbs,
          freeTemplates,
          premiumTemplates,
          page: currentPage,
          pageCount,
          total: numTemplates,
          relatives,
          replaced,
          tracking,
        }, // the updated filter data from the API
      } = action.payload;
      const filterSections = JSON.parse(JSON.stringify(state.filterSections));

      Object.keys(filterSections).forEach((name) => {
        // Find matching filter section
        const apiFilterSection = filterSectionsFromApi.find((afs) => afs.filterSectionId === name);

        if (!apiFilterSection) return;

        // Extract filter names
        const filterNames = Object.keys(filterSections[name].filters);

        Object.values(filterNames).forEach((filter) => {
          filterSections[name].filters = {...filterSections[name].filters};
          filterSections[name].filters[filter].on = false;
          const apiFilter = apiFilterSection.filters.find((f) => f.filterId === filter);
          if (!apiFilter) {
            // If the filter section is not in the API response or doesn't include the filter,
            // disable that filter option
            filterSections[name].filters[filter].enabled = false;
          } else {
            filterSections[name].filters[filter].enabled = apiFilter.enabled;
          }
        });
      });

      const templates = {
        free: freeTemplates.map((t, i) => ({...t, pos: t.pos || i})),
        premium: premiumTemplates,
      };

      return {
        ...state,
        filterSections,
        categoryId,
        categoryPath,
        hasChildren,
        parentId,
        templates,
        breadcrumbs: breadcrumbs ? getBreadCrumbs(breadcrumbs) : [],
        currentPage,
        pageCount,
        numTemplates,
        replaced,
        relatives: relatives || [],
        tracking: tracking || '',
      };
    },
  },
  selectors: {
    selectFilters: (state) => state.filterSections,
    selectGalleryCategoryId: (state) => state.categoryId,
    selectGalleryData: (state) => state,
    selectGalleryDataBreadcrumbs: (state) => state.breadcrumbs,
    selectGalleryDataCurrentPage: (state) => state.currentPage,
    selectGalleryDataPageCount: (state) => state.pageCount,
    selectGalleryDataPageDisplayName: (state) => state.pageDisplayName,
    selectGalleryDataPreviousCurrentPage: (state) => state.previousCurrentPage,
    selectGalleryDataRelatives: (state) => state.relatives,
    selectGalleryDataTracking: (state) => state.tracking,
    selectHasFilters: (state) =>
      Object.keys(state.filterSections).length > 0 && // There are filter groups defined
      Object.values(state.filterSections).some((f) => Object.keys(f.filters).length > 0), // Each group has filters
    selectIsGalleryDataReplaced: (state) => state.replaced,
    selectNumTemplates: (state) => (state.replaced ? 0 : state.numTemplates),
    selectTemplates: (state) => state.templates,
  },
});

export const galleryDataReducer = galleryDataSlice.reducer;
export const {initGalleryData, setPreviousCurrentPage, updateActiveFilters, resetFilters} =
  galleryDataSlice.actions;
export const {
  selectFilters,
  selectGalleryCategoryId,
  selectGalleryData,
  selectGalleryDataBreadcrumbs,
  selectGalleryDataCurrentPage,
  selectGalleryDataPageCount,
  selectGalleryDataPageDisplayName,
  selectGalleryDataPreviousCurrentPage,
  selectGalleryDataRelatives,
  selectGalleryDataTracking,
  selectHasFilters,
  selectIsGalleryDataReplaced,
  selectNumTemplates,
  selectTemplates,
} = galleryDataSlice.selectors;
